<ng-template #singleUpgrade let-flight="flight" let-cabinClass="cabinClass">
  <ng-container
    *ngIf="passengers$ | async as passengers"
    [ngTemplateOutlet]="(showPaymentMethods(cabinClass, flight) | async) ? paymentMethods : boundStatusInfo"
    [ngTemplateOutletContext]="{
      flight: flight,
      cabin: cabinClass,
      passengers: passengers,
      upgrade: getUpgradeByCabin(cabinClass, flight.upgrades),
    }"
  >
  </ng-container>
</ng-template>

<ng-template #notAvailable let-flight="flight">
  <fcom-notification [iconName]="SvgLibraryIcon.INFO" [theme]="NotificationTheme.INFO">
    {{ getNotAvailableReason(flight) | finLocalization }}
  </fcom-notification>
</ng-template>

<ng-template #tabs let-flight="flight">
  <fcom-tabs
    *ngIf="getTabSettings(flight) | async as tabSettings"
    [theme]="TabTheme.CLASSIC"
    [layoutType]="TabLayoutType.STRETCH"
    [prevButtonAriaLabel]="'actions.back' | finLocalization"
    [nextButtonAriaLabel]="'actions.next' | finLocalization"
    (clickedTab)="emitTabChange($event, flight.segmentInfo.flightId)"
    data-testid="upgrade-header"
  >
    <fcom-tab
      *ngIf="getUpgradeByCabin(Cabin.Ecopremium, flight.upgrades)"
      [title]="tabSettings[Cabin.Ecopremium].title | finLocalization"
      [subtitle]="tabSettings[Cabin.Ecopremium].subtitle"
      [active]="tabSettings[Cabin.Ecopremium].active"
      [data]="Cabin.Ecopremium"
      [attr.data-cabin]="Cabin.Ecopremium"
      [tabTitleDataTestId]="'tab-upgrade-to-' + Cabin.Ecopremium"
      data-testid="ecopremium"
    >
      <ng-container
        [ngTemplateOutlet]="singleUpgrade"
        [ngTemplateOutletContext]="{
          flight: flight,
          cabinClass: Cabin.Ecopremium,
        }"
      ></ng-container>
    </fcom-tab>
    <fcom-tab
      *ngIf="getUpgradeByCabin(Cabin.Business, flight.upgrades)"
      [title]="tabSettings[Cabin.Business].title | finLocalization"
      [subtitle]="tabSettings[Cabin.Business].subtitle"
      [active]="tabSettings[Cabin.Business].active"
      [data]="Cabin.Business"
      [attr.data-cabin]="Cabin.Business"
      [tabTitleDataTestId]="'tab-upgrade-to-' + Cabin.Business"
      data-testid="business"
    >
      <ng-container
        [ngTemplateOutlet]="singleUpgrade"
        [ngTemplateOutletContext]="{
          flight: flight,
          cabinClass: Cabin.Business,
        }"
      ></ng-container>
    </fcom-tab>
  </fcom-tabs>
</ng-template>

<ng-container *ngFor="let flight of bounds; let last = last; let first = first; trackBy: trackByFn">
  <fin-upgrade-bound-card
    [flight]="flight"
    [allFlights]="bounds"
    [numberOfFlights]="getNumberOfFlights(flight)"
    [first]="first"
    [last]="last"
    [bordered]="!shouldRenderTabs(flight.upgrades)"
    data-testid="upgrade-bound-card"
  >
    <ng-container
      *ngTemplateOutlet="shouldRenderTabs(flight.upgrades) ? tabs : notAvailable; context: { flight: flight }"
    >
    </ng-container>
  </fin-upgrade-bound-card>
</ng-container>

<ng-template #boundStatusInfo let-cabin="cabin" let-flight="flight" let-passengers="passengers">
  <ng-container *ngIf="getUpgradeStatusNotificationConfig(cabin, flight, passengers) as upgradeStatus">
    <div class="fill ps-medium border-left border-right border-bottom rounded-bottom-large">
      <fcom-notification class="ps-medium ps-large-t" [theme]="upgradeStatus.theme" [iconName]="upgradeStatus.icon">
        {{ upgradeStatus.text | finLocalization: { amount: upgradeStatus.numberOfSeats } }}
      </fcom-notification>
    </div>
  </ng-container>
</ng-template>

<ng-template #paymentMethods let-flight="flight" let-cabin="cabin" let-upgrade="upgrade">
  <div
    data-payment-form
    [formGroup]="paymentForm"
    class="fill ps-medium ps-large-t border-left border-right border-bottom rounded-bottom-large"
    [attr.data-flight]="flight.segmentInfo.flightId"
    [attr.data-upgrade]="upgrade.upgradeProductName"
  >
    <div [formGroupName]="flight.segmentInfo.flightId">
      <fcom-notification
        *ngIf="showWaitlistInfo(cabin, flight)"
        class="mr-xsmall-y bound-selection-notes"
        [theme]="NotificationTheme.INFO"
        [iconName]="SvgLibraryIcon.INFO"
        [linkText]="'readMore' | finLocalization"
        (linkClick)="openModal($event, flight.segmentInfo.flightId, upgrade.upgradeProductName)"
        data-testid="upgrade-waitlist-notification"
      >
        {{ 'oneUpgrade.notifications.waitlistedUpgrade' | finLocalization }}
      </fcom-notification>
      <fcom-block-selection-group
        [name]="flight.segmentInfo.flightId"
        [heading]="'oneUpgrade.paymentGroup.heading'"
        [hiddenText]="'oneUpgrade.paymentGroup.ariaText'"
        [formArrayName]="cabin"
      >
        <ng-container
          *ngFor="
            let option of upgrade.paymentOptions | keyvalue: moneyAsLast;
            let i = index;
            trackBy: trackPayments.bind(this, flight.segmentInfo.flightId)
          "
        >
          <fcom-block-selection
            class="relative flex flex--middle flex--end"
            [name]="cabin"
            [formControlName]="i"
            (change)="emitChange($event, i, flight, option, upgrade.upgradeProductName)"
          >
            <div class="flex flex--row flex-1">
              <div class="flex flex--column flex-1">
                <span class="is-vishidden">{{ 'oneUpgrade.aria.paymentType' | finLocalization }}</span>
                <div class="light-type font-body-2 ms-xxsmall-b" [attr.data-testid]="'pay-upgrade-with-' + option.key">
                  {{ 'oneUpgrade.paymentOptions.' + option.key | finLocalization }}
                </div>
                <span class="is-vishidden">{{ 'oneUpgrade.aria.amount' | finLocalization }}</span>
                <div class="bold-type font-body-1 nordic-blue-900-text">
                  {{ printPaymentValue(flight, upgrade.upgradeProductName, option.key) | async }}
                </div>
              </div>
            </div>

            <ng-container *ngIf="showWaitlistInfo(cabin, flight)">
              <fcom-tag
                aria-hidden="true"
                data-testid="upgrade-waitlist-payment"
                class="waitlist-info-chip ps-xsmall-r"
              >
                {{ 'oneUpgrade.paymentGroup.waitlist' | finLocalization }}
              </fcom-tag>
              <span class="is-vishidden">{{ 'oneUpgrade.aria.waitlist' | finLocalization }}</span>
            </ng-container>
            <ng-container tooltip>
              <button
                *ngIf="isDisabled(flight.segmentInfo.flightId, cabin, i)"
                type="button"
                class="icon absolute flex ps-small-r"
                fcomPopover
                [content]="tooltipContent"
                [ariaCloseText]="'actions.close' | finLocalization"
                [attr.aria-label]="'actions.info' | finLocalization"
              >
                <fcom-icon class="icon-medium" [name]="SvgLibraryIcon.HELP_BLOCK"></fcom-icon>
              </button>
              <ng-template #tooltipContent>
                <div
                  *ngIf="getTooltip(flight, upgrade.upgradeProductName, option.key, i) | async as tooltipName"
                  [ngSwitch]="tooltipName"
                  class="align-left nordic-blue-900-text"
                >
                  <ng-container *ngSwitchCase="TooltipType.NotEnoughPoints">
                    <h2 class="font-body-1 bold-type nordic-blue-900-text">
                      {{ 'oneUpgrade.tooltip.notEnoughPointsTitle' | finLocalization }}
                    </h2>
                    <p class="pr-xxsmall-t">
                      {{ 'oneUpgrade.tooltip.notEnoughPointsText' | finLocalization }}
                    </p>
                  </ng-container>
                  <ng-container *ngSwitchCase="TooltipType.NotEnoughVouchers">
                    <h2 class="font-body-1 bold-type nordic-blue-900-text">
                      {{ 'oneUpgrade.tooltip.notEnoughVouchersTitle' | finLocalization }}
                    </h2>
                    <p class="pr-xxsmall-t">
                      {{ 'oneUpgrade.tooltip.notEnoughVouchersText' | finLocalization }}
                    </p>
                  </ng-container>
                  <ng-container *ngSwitchCase="TooltipType.NoCardPayment">
                    <h2 class="font-body-1 bold-type nordic-blue-900-text">
                      {{ 'oneUpgrade.tooltip.cardUnavailableTitle' | finLocalization }}
                    </h2>
                    <p class="pr-xxsmall-t">
                      {{ 'oneUpgrade.tooltip.cardUnavailableText' | finLocalization }}
                    </p>
                  </ng-container>
                  <ng-container *ngSwitchCase="TooltipType.CantCombine">
                    <h2 class="font-body-1 bold-type nordic-blue-900-text">
                      {{ 'oneUpgrade.tooltip.cannotBeCombinedTitle' | finLocalization }}
                    </h2>
                    <p class="pr-xxsmall-t">
                      {{ 'oneUpgrade.tooltip.cannotBeCombinedTextFirst' | finLocalization }}
                    </p>
                    <p class="pr-xxsmall-t">
                      {{ 'oneUpgrade.tooltip.cannotBeCombinedTextSecond' | finLocalization }}
                    </p>
                  </ng-container>
                  <ng-container *ngSwitchCase="TooltipType.NotEnoughLongHaulVouchersForPax">
                    <h2 class="font-body-1 bold-type nordic-blue-900-text">
                      {{ 'oneUpgrade.tooltip.notEnoughLongHaulVouchersForPaxTitle' | finLocalization }}
                    </h2>
                    <p class="pr-xxsmall-t">
                      {{ 'oneUpgrade.tooltip.notEnoughLongHaulVouchersForPaxText' | finLocalization }}
                    </p>
                  </ng-container>
                </div>
              </ng-template>
            </ng-container>
          </fcom-block-selection>
        </ng-container>
      </fcom-block-selection-group>
      <div class="font-body-1 ps-xsmall-y flex flex--end">
        <span *ngIf="passengers$ | async as passengers" class="font-body-1" data-testid="upgrade-cta-points">
          {{
            'oneUpgrade.upgradeSelection' + (passengers === 1 ? '.pricesForPassenger' : '.pricesForPassengers')
              | finLocalization: { passengerAmount: passengers }
          }}
        </span>
      </div>
    </div>
  </div>
</ng-template>
